/**
 * AddBulk Component
 * Main component for bulk CSV upload functionality
 */

import React, { useRef, useMemo } from "react";
import { Spinner } from "components/Spinner/Spinner";
import UploadIcon from "assets/Icon_Upload.png";
import WarningIcon from "assets/Icon_Warning.png";
import errorImage from "assets/Icon_Notification_Warning.png";

import { Form, Formik, FormikProps } from "formik";
import { PanelButtons } from "components/helpers/panelHelpers/PanelButtons";
import ToastMessage from "components/ToastMessages/ToastMessage";
import capitaliseStringFirstLetter from "helpers/capitaliseStringFirstLetter";

import UploadCSV from "./components/UploadCSV";
import ContactImportForm from "./components/ConditionalRenderers/ContactImportForm";
import SurveyImportForm from "./components/ConditionalRenderers/SurveyImportForm";

import { getValidationSchema } from "./AddBulk.validation";
import { AddBulkProps, PAGES } from "./AddBulk.types";
import { useAddBulkForm } from "./AddBulk.hooks";
import { exportErrorsToFile, parseErrorItemDisplay, downloadFile } from "./AddBulk.utils";

export { PAGES } from "./AddBulk.types";

/**
 * Legacy export for backward compatibility
 * @deprecated Use downloadFile from AddBulk.utils instead
 */
export const downloadUploadedFile = downloadFile;

const AddBulk: React.FC<AddBulkProps> = ({
  fieldName,
  fieldLabel,
  url,
  noun,
  fileInstructions,
  downloadFile,
  onDone,
  isRedirected,
  parentSelectedIdName,
  parentSelectedId,
  extraPayloadItems,
}) => {
  // Use custom hooks for state management and logic
  const formState = useAddBulkForm(
    noun,
    url,
    parentSelectedIdName,
    parentSelectedId,
    extraPayloadItems,
    onDone
  );

  const {
    loading,
    initialData,
    contactAccessTypes,
    contactTypes,
    programmes,
    surveyTemplates,
    csvFile,
    uploadError,
    uploadErrorMessages,
    success,
    downloadError,
    handleFileChange,
    resetFile,
    handleDownload,
    handleSubmit,
  } = formState;

  const formikRef = useRef<FormikProps<any>>(null);

  // Memoize validation schema to prevent unnecessary recalculations
  const validationSchema = useMemo(
    () => getValidationSchema(noun, contactAccessTypes),
    [noun, contactAccessTypes]
  );

  if (loading) {
    return (
      <div data-testid="addbulk-loading">
        <Spinner positionAbsolute />
      </div>
    );
  }

  return (
    <div className="AddBulk" data-testid="addbulk-container">
      <Formik
        initialValues={initialData}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={true}
        validateOnMount={false}
        innerRef={formikRef}
        enableReinitialize
      >
        {(formik) => (
          <Form className="">
            <div className="Content">
              {noun === PAGES.CONTACT && (
                <ContactImportForm
                  isRedirected={isRedirected}
                  parentSelectedId={parentSelectedId}
                />
              )}
              {noun === PAGES.SURVEY && (
                <SurveyImportForm
                  actions={formState.initialData.actions || []}
                  programmes={programmes}
                  surveyTemplates={surveyTemplates}
                />
              )}

              <div
                className="Btn DownloadBtn"
                onClick={handleDownload}
                data-testid="download-template-btn"
              >
                <div className="Text">Download template</div>
              </div>

              {downloadError && (
                <div className="ErrorContainer" data-testid="download-error">
                  <div className="Title">Error:</div>
                  <div className="Text">Failed to download CSV file, please try again later.</div>
                </div>
              )}

              <h4>{`Upload ${noun} files`}</h4>
              <UploadCSV
                field={fieldName}
                label={fieldLabel}
                readOnly={false}
                onFileUploadEnd={handleFileChange}
                file={csvFile}
                resetFile={resetFile}
                maximumFileSizeInMB={10}
                maximumNoOfFiles={1}
                resetButtonText="Reset"
                downloadableFile={downloadFile}
                downloadButtonText="Download"
                dropzoneText={"CSV file"}
                uploadIcon={UploadIcon}
                warningIcon={WarningIcon}
                fileInstructions={fileInstructions}
                showReset={true}
              />

              {noun === PAGES.CONTACT && contactTypes.length > 0 && (
                <div className="PossibleContactTypes" data-testid="contact-types-list">
                  <p>Possible Contact Types</p>
                  {contactTypes.map((ct, index) => (
                    <p key={index}>- {ct.label}</p>
                  ))}
                </div>
              )}

              {noun === PAGES.SURVEY &&
                uploadError &&
                uploadErrorMessages &&
                uploadErrorMessages.length > 0 && (
                  <div>
                    <button
                      className="Btn BtnAdd BtnCSV"
                      onClick={() => exportErrorsToFile(uploadErrorMessages, "errors.txt")}
                      data-testid="export-errors-btn"
                    >
                      Export Errors
                    </button>
                  </div>
                )}

              <br />
              {uploadError && (
                <div className="ErrorContainer" data-testid="upload-error">
                  <img src={errorImage} alt="error icon" className="ErrorImage" />

                  {uploadErrorMessages && uploadErrorMessages.length > 0 ? (
                    <>
                      {uploadErrorMessages.map((item, index) => {
                        const unescapedItem = item.replace(/\\n/g, "\n");
                        const displayItem =
                          noun === PAGES.SURVEY
                            ? unescapedItem
                            : item.includes("\n")
                              ? formatErrorDisplay(item)
                              : item;
                        return (
                          <div
                            key={index}
                            className="Text"
                            style={{ whiteSpace: "pre-line" }}
                            data-testid={`error-message-${index}`}
                          >
                            {displayItem}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div className="Text">Failed to upload CSV file, please try again later.</div>
                  )}
                </div>
              )}
            </div>

            <PanelButtons close={onDone} submitButtonText="Submit" />
          </Form>
        )}
      </Formik>

      {success?.countUploaded && (
        <ToastMessage
          title="Success"
          text={`${capitaliseStringFirstLetter(noun)}s uploaded: ${
            success.countUploaded
          } \n ${capitaliseStringFirstLetter(noun)}s inserted: ${
            success.countInserted
          } \n ${capitaliseStringFirstLetter(noun)}s duplicated: ${success.countDuplicates}`}
          bSuccess={true}
        />
      )}
    </div>
  );
};

export default AddBulk;

/**
 * Format error message for display with proper line breaks
 * @param item - Error message string
 * @returns Formatted JSX element
 */
const formatErrorDisplay = (item: string) => {
  const parsedItems = parseErrorItemDisplay(item);
  return parsedItems.map((parsed, index) => (
    <div className="LineBreak" key={index}>
      <span className="ColumnName TextItem">{parsed.columnName}: </span>
      <span className="TextItem">{parsed.value}</span>
    </div>
  ));
};
